import FileRenderer from '@features/FileRenderer';
import React from 'react';
import { useGetDataRecordVersionQuery } from '../../apiHooks/dataRecordApi';
import { useGetFileTreeNodeQuery } from '../../apiHooks/filesApi';
import { normalizeCssDimension } from './utils';

interface IProps {
    src: string;
    resourceVersionUuid: string;
    width?: string;
    height?: string;
    alt?: string;
    figureCaption?: string;
    isFigure?: boolean;
}

export default function ResourceAssetFileRenderer(props: IProps) {
    const { resourceVersionUuid, src, figureCaption, isFigure } = props;
    const assetPrefix = 'assets://';
    const path = src.startsWith(assetPrefix) ? src.slice(assetPrefix.length) : src;

    const resourceVersionQuery = useGetDataRecordVersionQuery({ resourceVersionUuid });
    const presignedStorageUrl = resourceVersionQuery.data?.presigned_download_url;

    const { data: fileNode } = useGetFileTreeNodeQuery(
        {
            path,
            presignedStorageUrl,
        },
        { skip: !presignedStorageUrl },
    );

    if (!fileNode) {
        return <pre>{`File "${assetPrefix}${path}" ${props.alt ? `[${props.alt}] ` : ''}not found`}</pre>;
    }

    const shouldAutoResize = !props.height || props.height === 'auto';
    const wrapperStyle: React.CSSProperties = {
        width: normalizeCssDimension(props.width) || '100%',
        height: shouldAutoResize ? 'auto' : normalizeCssDimension(props.height) || '500px',
    };

    if (figureCaption) {
        return (
            <figure style={wrapperStyle} className='flex flex-col items-center justify-around'>
                <FileRenderer
                    allowOpenUrlForAnyRemoteHost={true}
                    allowScrollNavigationOnHover={true}
                    fileTreeNode={fileNode}
                    presignedStorageUrl={presignedStorageUrl}
                    enableDebouncedAutoResize={shouldAutoResize}
                    centerImage={isFigure}
                />
                <figcaption className='text-sm text-gray-600 mt-2 text-center italic'>{figureCaption}</figcaption>
            </figure>
        );
    }

    return (
        <div style={wrapperStyle} className='my-6'>
            <FileRenderer
                allowOpenUrlForAnyRemoteHost={true}
                allowScrollNavigationOnHover={true}
                fileTreeNode={fileNode}
                presignedStorageUrl={presignedStorageUrl}
                enableDebouncedAutoResize={shouldAutoResize}
                centerImage={isFigure}
            />
        </div>
    );
}
